"use client"

import type React from "react"
import { useCallback, useEffect, useId, useRef, useState } from "react"

import { Check, Loader2, LocateFixed } from "lucide-react"

import { cn } from "@/lib/utils"
import { geocodeZip } from "@/lib/location-resolver-client"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"

export type ZipValidationResult =
  | { status: 'empty'; zip: '' }
  | { status: 'format-invalid'; zip: string }
  | { status: 'resolving'; zip: string }
  | { status: 'resolved'; zip: string; city?: string }
  | { status: 'invalid'; zip: string }

interface ZipCodeInputProps {
  value: string
  onChange: (zip: string, meta?: { entryMethod?: 'typed' | 'saved' | 'geolocation' }) => void
  /** Reports the existing ZIP lookup result so a parent can gate submission
   * without issuing its own duplicate request. */
  onValidationChange?: (result: ZipValidationResult) => void
  error?: string
  disabled?: boolean
  label?: string
  showLabel?: boolean
  placeholder?: string
  id?: string
  className?: string
  enableGeolocation?: boolean
  /**
   * 'icon' (default): compact locate icon inside the input.
   * 'button': a labeled full-width "Use my location" button below the input —
   * for flows where geolocation should be a first-class path (the analyzer's
   * ZIP step: PostHog showed a ~7% drop at ZIP entry and the icon variant was
   * effectively invisible).
   */
  geolocationVariant?: 'icon' | 'button'
  autoFocus?: boolean
  rememberLastZip?: boolean
}

export function ZipCodeInput({
  value,
  onChange,
  onValidationChange,
  error,
  disabled = false,
  label = "ZIP code",
  showLabel = true,
  placeholder = "e.g. 30309",
  id,
  className,
  enableGeolocation = true,
  geolocationVariant = 'icon',
  autoFocus = false,
  rememberLastZip = false,
}: ZipCodeInputProps) {
  const reactId = useId()
  const inputId = id ?? `zip-input-${reactId}`
  const inputErrorId = `${inputId}-error`
  const geoErrorId = `${inputId}-geo-error`

  const [resolution, setResolution] = useState<ZipValidationResult>({ status: 'empty', zip: '' })
  const [geolocating, setGeolocating] = useState(false)
  const [geoError, setGeoError] = useState("")

  const { savedInputs, saveInputs, clearInputs } = useSavedInputs()
  const hasAppliedSaved = useRef(false)
  const geoInFlightRef = useRef(false)
  const validationCallbackRef = useRef(onValidationChange)

  const isZipFormatValid = /^\d{5}$/.test(value)
  const isResolvedZip =
    resolution.status === 'resolved' && resolution.zip === value
  const isResolvingZip =
    resolution.status === 'resolving' && resolution.zip === value

  useEffect(() => {
    validationCallbackRef.current = onValidationChange
  }, [onValidationChange])

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!autoFocus) return
    const el = inputRef.current
    if (!el) return
    el.focus({ preventScroll: true })
    // Skip the centering scroll when the input is already fully visible —
    // otherwise desktop users see a tiny snap on every upload→ZIP transition
    // even though the card is already on-screen. Header is `scroll-mt-20`
    // (5rem) so 80px keeps the input below the sticky header band.
    const rect = el.getBoundingClientRect()
    const fullyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight
    if (!fullyVisible) {
      el.scrollIntoView({ behavior: "auto", block: "center" })
    }
  }, [autoFocus])

  // Prefill from localStorage if requested and no externally provided value.
  useEffect(() => {
    if (!rememberLastZip) return
    if (hasAppliedSaved.current) return
    if (value) return
    const saved = savedInputs.zip
    if (!saved || !/^\d{5}$/.test(saved)) return
    hasAppliedSaved.current = true
    onChange(saved, { entryMethod: 'saved' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.zip])

  // Persist valid ZIPs back to localStorage when remembering.
  useEffect(() => {
    if (!rememberLastZip) return
    if (!isResolvedZip) return
    if (error) return
    saveInputs({ zip: value })
  }, [value, isResolvedZip, rememberLastZip, saveInputs, error])

  // Resolve the completed ZIP once and expose that result to the parent. A
  // five-digit shape is not treated as proof that the ZIP exists.
  useEffect(() => {
    if (!value) {
      const next: ZipValidationResult = { status: 'empty', zip: '' }
      setResolution(next)
      validationCallbackRef.current?.(next)
      return
    }
    if (!isZipFormatValid) {
      const next: ZipValidationResult = { status: 'format-invalid', zip: value }
      setResolution(next)
      validationCallbackRef.current?.(next)
      return
    }

    let cancelled = false
    const resolving: ZipValidationResult = { status: 'resolving', zip: value }
    setResolution(resolving)
    validationCallbackRef.current?.(resolving)

    geocodeZip(value).then((loc) => {
      if (cancelled) return
      const next: ZipValidationResult = loc
        ? { status: 'resolved', zip: value, city: loc.city || undefined }
        : { status: 'invalid', zip: value }
      setResolution(next)
      validationCallbackRef.current?.(next)
    })
    return () => {
      cancelled = true
    }
  }, [value, isZipFormatValid])

  // Remove only a matching legacy invalid saved ZIP. A newly typed invalid ZIP
  // never replaces a previously verified saved value.
  useEffect(() => {
    if (!rememberLastZip) return
    if (resolution.status !== 'invalid' || resolution.zip !== value) return
    if (savedInputs.zip !== value) return
    clearInputs(['zip'])
  }, [clearInputs, rememberLastZip, resolution, savedInputs.zip, value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeZip(e.target.value)
    onChange(next, { entryMethod: 'typed' })
  }

  const handleUseMyLocation = useCallback(async () => {
    // Synchronous guard — blocks a second click before React commits
    // `geolocating: true`, so the disabled attribute can't race.
    if (geoInFlightRef.current) return
    geoInFlightRef.current = true

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Your browser doesn't support location detection")
      geoInFlightRef.current = false
      return
    }

    setGeolocating(true)
    setGeoError("")

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
      })
      const { latitude, longitude } = position.coords
      const res = await fetch(`/api/location/reverse?lat=${latitude}&lon=${longitude}`)
      const data = await res.json()
      if (res.ok && data.zip) {
        onChange(data.zip, { entryMethod: 'geolocation' })
      } else {
        setGeoError("We couldn't match that to a U.S. ZIP code")
      }
    } catch (err) {
      const code = (err as { code?: number } | null)?.code
      const denied = code === 1
      setGeoError(
        denied
          ? "Location permission was blocked. Enter your ZIP above."
          : "Couldn't get your location. Enter your ZIP above.",
      )
    } finally {
      setGeolocating(false)
      geoInFlightRef.current = false
    }
  }, [onChange])

  const cityEcho = isResolvedZip
    ? resolution.city || "ZIP verified"
    : null

  const resolutionError =
    resolution.status === 'invalid' && resolution.zip === value
      ? "We couldn't find that U.S. ZIP code. Check it and try again."
      : undefined
  const effectiveError = error || resolutionError
  const showCityEcho = isResolvedZip && !effectiveError && cityEcho !== null
  const showGeoButton = enableGeolocation && !showCityEcho && !isResolvingZip && geolocationVariant === 'icon'
  const showGeoLabeledButton = enableGeolocation && !showCityEcho && !isResolvingZip && geolocationVariant === 'button'

  return (
    <div className={cn("text-left", className)}>
      {showLabel && (
        <label
          htmlFor={inputId}
          className="block text-xs font-semibold text-gray-900 mb-2 tracking-[0.02em]"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          {...ZIP_INPUT_ATTRS}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={showLabel ? undefined : label}
          aria-invalid={effectiveError ? "true" : "false"}
          aria-busy={isResolvingZip || undefined}
          aria-describedby={effectiveError ? inputErrorId : geoError ? geoErrorId : undefined}
          className={cn(
            "scroll-mt-24",
            "ph-no-capture ph-mask",
            "w-full pl-4 py-3.5 rounded-[10px] text-base font-medium text-gray-900",
            "tabular-nums tracking-[0.02em]",
            "border-[1.5px] bg-white outline-hidden transition-colors",
            "placeholder:text-gray-400",
            disabled && "opacity-60 cursor-not-allowed",
            showCityEcho ? "pr-32 sm:pr-36" : showGeoButton || isResolvingZip ? "pr-12" : "pr-3.5",
            effectiveError
              ? "border-red-300 focus:border-red-500"
              : isResolvedZip
                ? "border-green-600"
                : "border-gray-300 focus:border-green-500",
          )}
        />
        {showCityEcho ? (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-action pointer-events-none">
            <Check className="w-3.5 h-3.5 flex-none" strokeWidth={3} />
            <span className="max-w-[110px] truncate sm:max-w-[140px]">{cityEcho}</span>
          </div>
        ) : isResolvingZip ? (
          <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-green-600" aria-hidden="true">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : showGeoButton ? (
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={disabled || geolocating}
            aria-label="Use my location"
            title="Use my location"
            className={cn(
              "absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-md transition-colors",
              "text-green-600 hover:bg-green-50 focus:outline-hidden focus:ring-2 focus:ring-green-500/30",
              geolocating ? "cursor-wait opacity-70" : "cursor-pointer",
            )}
          >
            {geolocating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LocateFixed className="w-4 h-4" />
            )}
            <span className="sr-only">{geolocating ? "Locating…" : "Use my location"}</span>
          </button>
        ) : null}
      </div>
      {showGeoLabeledButton && (
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={disabled || geolocating}
          aria-describedby={geoError ? geoErrorId : undefined}
          className={cn(
            "mt-2 w-full inline-flex items-center justify-center gap-2 min-h-[44px] rounded-[10px]",
            "border-[1.5px] border-green-600/30 bg-white text-sm font-semibold text-green-700",
            "hover:bg-green-50 hover:border-green-600/50 transition-colors",
            "focus:outline-hidden focus:ring-2 focus:ring-green-500/30",
            geolocating ? "cursor-wait opacity-70" : "cursor-pointer",
            disabled && "opacity-60 cursor-not-allowed",
          )}
        >
          {geolocating ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <LocateFixed className="w-4 h-4" aria-hidden="true" />
          )}
          {geolocating ? "Finding your ZIP…" : "Use my location instead"}
        </button>
      )}
      <span aria-live="polite" className="sr-only">
        {isResolvingZip
          ? "Checking ZIP code"
          : showCityEcho && cityEcho
            ? `Matched ${cityEcho}`
            : ""}
      </span>
      {effectiveError && <p id={inputErrorId} role="alert" className="text-red-600 text-sm mt-2">{effectiveError}</p>}
      {!effectiveError && geoError && (
        <p id={geoErrorId} role="alert" className="text-amber-600 text-sm mt-2">
          {geoError}
        </p>
      )}
    </div>
  )
}
